import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Bell, Check, Clock, AlertTriangle, Info, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { IoCheckmarkDone } from "react-icons/io5";

const notificationTypes = {
    success: {
        icon: Check,
        className: "text-green-500 bg-green-500/10",
    },
    warning: {
        icon: AlertTriangle,
        className: "text-yellow-500 bg-yellow-500/10",
    },
    error: {
        icon: AlertTriangle,
        className: "text-red-500 bg-red-500/10",
    },
    info: {
        icon: Info,
        className: "text-blue-500 bg-blue-500/10",
    },
};

const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
};

const CompactNotificationItem = ({ notification, onRead, onClose }) => {
    const navigate = useNavigate();
    const { axiosInstance, setUnreadCount } = useAuth();
    const { icon: Icon, className } = notificationTypes[notification.type] || notificationTypes.info;

    const handleClick = async (e) => {
        e.preventDefault();
        
        if (!notification.read) {
            try {
                setUnreadCount(prev => Math.max(0, prev - 1));
                onRead(notification._id);
                await axiosInstance.post(`/notifications/${notification._id}/mark-read`);
            } catch (error) {
                setUnreadCount(prev => prev + 1);
                console.error('Error marking notification as read:', error);
            }
        }
    
        // Handle task and monitor navigation
        if (notification.metadata?.taskKey) {
            onClose();
            navigate(`/${notification.organizationId.slug}/plan/${notification.metadata.taskKey}`);
        } else if (notification.metadata?.monitorId) {
            onClose();
            navigate(`/monitors/${notification.metadata.monitorId}`);
        }
    };

    return (
        <DropdownMenuItem
            onClick={handleClick}
            className={cn(
                "flex items-center gap-3 px-3 py-3 mx-1 my-1 rounded-md cursor-pointer focus:bg-muted/50",
                !notification.read && "bg-primary/5"
            )}
        >
            {/* Icon with unread indicator */}
            <div className="relative shrink-0">
                <div className={cn("rounded-md p-1.5", className)}>
                    <Icon className="size-3.5" />
                </div>
                {!notification.read && (
                    <div className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-primary ring-2 ring-popover" />
                )}
            </div>
            <div className="flex-1 min-w-0 space-y-0.5">
                <p className={cn(
                    "text-sm leading-tight truncate",
                    !notification.read ? "font-medium" : "text-foreground"
                )}>
                    {notification.title}
                </p>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock className="size-2.5" />
                    <span>{formatRelativeTime(notification.timestamp)}</span>
                </div>
            </div>
            {(notification.metadata?.taskKey || notification.metadata?.monitorId) && (
                <ChevronRight className="size-4 text-muted-foreground/50 shrink-0" />
            )}
        </DropdownMenuItem>
    );
};

const NotificationDropdown = () => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const { axiosInstance, notifications, setNotifications, fetchUnreadCount, unreadCount } = useAuth();

    useEffect(() => {
        if (open) {
            fetchNotifications();
            fetchUnreadCount();
        }
    }, [open]);

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/notifications');
            setNotifications(response.data);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const markAllAsRead = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            await axiosInstance.post('/notifications/mark-all-read');
            setNotifications(prev =>
                prev.map(notif => ({ ...notif, read: true }))
            );
            fetchUnreadCount();
        } catch (error) {
            console.error('Error marking notifications as read:', error);
        }
    };

    const handleNotificationRead = (notificationId) => {
        setNotifications(prev =>
            prev.map(notif =>
                notif._id === notificationId
                    ? { ...notif, read: true }
                    : notif
            )
        );
    };

    const handleViewAll = (e) => {
        e.preventDefault();
        setOpen(false);
        navigate('/notifications');
    };

    // Show only the 5 most recent notifications
    const recentNotifications = notifications.slice(0, 5);
    const hasUnread = notifications.some(n => !n.read);

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative">
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                        <span className="absolute top-0.5 right-0.5 min-w-[14px] h-3.5 px-1 flex items-center justify-center bg-primary rounded-full text-[10px] font-medium text-primary-foreground">
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
                {/* Header */}
                <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-sm font-semibold">Notifications</span>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={markAllAsRead}
                        disabled={!hasUnread}
                        className="h-7 px-2 text-xs gap-1.5"
                    >
                        <IoCheckmarkDone className="size-3.5" />
                        Mark all read
                    </Button>
                </div>
                <DropdownMenuSeparator />

                {/* Notifications List */}
                <div>
                    {loading ? (
                        <div className="p-1">
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className="flex items-start gap-3 p-3">
                                    <Skeleton className="size-7 rounded-md shrink-0" />
                                    <div className="flex-1 space-y-2">
                                        <Skeleton className="h-3.5 w-4/5" />
                                        <Skeleton className="h-3 w-1/3" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : recentNotifications.length > 0 ? (
                        recentNotifications.map((notification) => (
                            <CompactNotificationItem
                                key={notification._id}
                                notification={notification}
                                onRead={handleNotificationRead}
                                onClose={() => setOpen(false)}
                            />
                        ))
                    ) : (
                        <div className="flex flex-col items-center justify-center py-6 text-center">
                            <Bell className="size-6 text-muted-foreground mb-2" />
                            <p className="text-sm text-muted-foreground">
                                No notifications yet
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <DropdownMenuSeparator />
                <button 
                    onClick={handleViewAll}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-accent"
                >
                    <span>{notifications.length} notification{notifications.length !== 1 ? 's' : ''}</span>
                    <span>View all →</span>
                </button>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default NotificationDropdown;

